<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';
  import Footer from '$lib/components/Footer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';

  let scrollY = 0;
</script>

<div class="min-h-screen bg-white">
  <ProjectHeroSplit
    title="Smart Materials & Autonomous Interactions"
    client="VW Future Center Europe"
    industry="Transportation"
    product="Designing intuitive interactions between autonomous vehicles and humans through smart materials and ambient technologies"
    heroMedia={{
      type: 'video',
      src: "https://vz-6b226b53-3c8.b-cdn.net/defc863b-b8f3-4a50-991d-77b3bd98ea63/playlist.m3u8"
    }}
    metrics={[
      { value: 8, label: "Weeks Research" },
      { value: 3, label: "Production Loops" },
      { value: 2, label: "Final Prototypes" }
    ]}
    roles="Project lead, design research, and prototyping for autonomous vehicle interactions."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Project Description -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90 animate-on-view">
        Making autonomous vehicles feel more alive than smart
      </p>
    </div>

    <!-- Image Gallery -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
      <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
        <img 
          src="/projects/meta-materials/IMG 0027 nand-web.jpg" 
          alt="NAND team working on smart materials"
          class="w-full h-full object-cover"
        />
      </div>
      <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
        <img 
          src="/projects/meta-materials/Meta Materi...Prototype.jpg" 
          alt="Meta Materials Prototype"
          class="w-full h-full object-cover"
        />
      </div>
      <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
        <img 
          src="/projects/meta-materials/Meta Materi...orschung.jpg" 
          alt="Meta Materials Research Process"
          class="w-full h-full object-cover"
        />
      </div>
      <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
        <img 
          src="/projects/meta-materials/IMG_0027.jpg" 
          alt="Detailed view of smart materials implementation"
          class="w-full h-full object-cover"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="mb-16 text-gray-700">
            As mobility evolves with electrical and self-driving cars, we needed to explore how technological innovations could be reflected in automobile interactions. The key challenge was to make persuasive ambient technologies look more "alive" than "smart" and help people adapt their behavior naturally.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Approach</h2>
          <p class="mb-16 text-gray-700">
            We developed a hands-on approach from start to finish, working with materials in close iteration cycles. Through rapid prototyping, evaluation, and continuous iteration, we created a new communication language that establishes emotional connections between drivers and vehicles.
          </p>
        </div>
      </div>
    </div>

    <!-- Process Visualization -->
    <div class="my-16">
      <h2 class="text-2xl font-light mb-8">Process & Development</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
          <img 
            src="/projects/meta-materials/MS 4.png" 
            alt="Development Process Initial Stage"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
          <img 
            src="/projects/meta-materials/MS 7.png" 
            alt="Development Process Stage 2"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="aspect-video rounded-lg overflow-hidden shadow-lg">
          <img 
            src="/projects/meta-materials/ms-3.png" 
            alt="Development Process Final Stage"
            class="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>

    <!-- Final Prototype -->
    <div class="my-16">
      <h2 class="text-2xl font-light mb-8">Final Prototype</h2>
      <div class="aspect-[21/9] rounded-lg overflow-hidden shadow-lg">
        <img 
          src="/projects/meta-materials/T89A9950.jpg" 
          alt="Final Smart Materials Prototype"
          class="w-full h-full object-cover"
        />
      </div>
    </div>

    <!-- Key Features -->
    <div class="w-full">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Research Focus</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 mb-16 bg-black">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 4c4.41 0 8 3.59 8 8s-3.59 8-8 8s-8-3.59-8-8s3.59-8 8-8m0-2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm0 13l-4-4h8z"/></svg>'
          title="Smart Materials"
          description="Integrating interactive materials into the design process from day one"
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8z"/></svg>'
          title="Alive vs Smart"
          description="Exploring the spectrum between ambient intelligence and emotional connection"
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74c0-3.86-3.14-7-7-7z"/></svg>'
          title="Communication Language"
          description="Developing intuitive interaction patterns for emotional vehicle-human connection"
          delay={0.4}
        />
      </div>
    </div>

    <!-- Process Section -->
    <div class="w-full animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Project Process</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 mb-16 bg-black">
        <div class="relative">
          <FeatureCell
            icon=""
            title="Production Loop 1"
            description="Rapid prototyping focused on quantity, generating multiple concepts and ideas"
            delay={0.2}
          />
          <FeatureCell
            icon=""
            title="Production Loop 2"
            description="Quality-focused iteration with technical development and UX refinement"
            delay={0.4}
          />
        </div>

        <div class="relative">
          <FeatureCell
            icon=""
            title="Documentation"
            description="Comprehensive documentation of research insights and prototypes"
            delay={0.6}
          />
          <FeatureCell
            icon=""
            title="Final Delivery"
            description="Polished prototypes with visual materials and future applications"
            delay={0.8}
          />
        </div>
      </div>
    </div>
  </div>

  <ProjectNav 
    prevProject={{ title: "Known Unknowns", url: "/projects/known-unknowns" }}
    nextProject={{ title: "Is This Thing On?", url: "/projects/is-this-thing-on" }}
  />

  <Footer />
</div>

<style>
  .project-section {
    padding: 2rem 0;
    margin: 0 auto;
    max-width: 1280px;
  }

  :global(.animate-on-view) {
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  :global(.animate-on-view.in-view) {
    opacity: 1;
    transform: translateY(0);
  }
</style> 